/**
 * Per-slug content overrides for /brand/[name]/ pages that have had
 * dedicated keyword research done. Falls back to the generic template
 * in BrandPage.tsx for every slug not listed here.
 */

import { FAQ, focusFaqs, porscheFaqs } from './faqData';

export interface BrandPageOverride {
  h1?: string;
  intro?: string;
  /** Overrides the "Popular {Brand} Models Use Green Filter" heading in the Models section. */
  modelsHeading?: string;
  /** Overrides the "Below are the {Brand} models..." paragraph in the Models section. */
  modelsParagraph?: string;
  /** Per-model product heading override, keyed by the API's exact model.title (e.g. '911', 'Boxster'). */
  modelHeadings?: Record<string, string>;
  faqs?: FAQ[];
  faqTitle?: string;
}

export const BRAND_PAGE_OVERRIDES: Record<string, BrandPageOverride> = {
  focus: {
    h1: 'Ford Focus Air Filter',
    intro:
      "Whether your Focus is a 2007, 2008, 2009, 2010, 2012, or 2013 model, or a newer 2014-2017 Focus, ST, or RS, Green Filter makes a washable, reusable Ford Focus air filter built to fit your engine. It's a smarter Ford Focus air filter replacement: wash it, re-oil it, and reinstall it instead of buying a new one every few months.",
    faqs: focusFaqs,
    faqTitle: 'Ford Focus Air Filter FAQs',
  },
  mustang: {
    h1: 'Mustang Air Filter',
  },
  'mercedes-amg': {
    h1: 'Mercedes-AMG Air Filters',
  },
  porsche: {
    h1: 'Porsche Air Filters',
    intro:
      'Green Filter makes a washable, reusable Porsche air filter engineered as a high-flow performance engine air filter for 911 (996, 997), 928, Boxster (986, 987, 981), and Cayman (987, 981) models. Skip the recurring Porsche air filter replacement - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      '911': 'Porsche 911 Air Filter',
      '928': 'Porsche 928 Air Filter',
      '997': 'Porsche 997 Air Filter',
      'Boxster': 'Porsche Boxster Air Filter',
      'Cayman': 'Porsche Cayman Air Filter',
    },
    faqs: porscheFaqs,
    faqTitle: 'Porsche Air Filter FAQs',
  },
  'subaru-wrx': {
    h1: 'Subaru WRX Air Filter',
    intro:
      'Green Filter makes a washable, reusable WRX air filter engineered as a high-flow performance engine air filter for Subaru WRX and WRX STI models. Find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
  },
  viper: {
    h1: 'Dodge Viper Air Filter',
    intro:
      'Green Filter makes a washable, reusable Viper air filter engineered as a high-flow performance engine air filter for Dodge Viper models. Find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
  },
  'nissan-trucks': {
    h1: 'Nissan Truck Air Filters',
    intro:
      'Green Filter makes a washable, reusable Nissan Frontier air filter and Nissan Navara air filter engineered as high-flow performance engine air filters. Whether you need a Nissan Frontier engine air filter or a Nissan Navara air filter, find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      Navara: 'Nissan Navara Air Filter',
      Frontier: 'Nissan Frontier Air Filter',
    },
  },
  'toyota-trucks': {
    modelHeadings: {
      HiLux: 'Toyota HiLux Air Filter',
      Tacoma: 'Toyota Tacoma Air Filter',
    },
  },
  ferrari: {
    modelHeadings: {
      '360': 'Ferrari 360 Air Filter',
      '430': 'Ferrari 430 Air Filter',
      F430: 'Ferrari F430 Air Filter',
    },
  },
  infiniti: {
    modelHeadings: {
      G35: 'Infiniti G35 Engine Air Filter',
      I35: 'Infiniti I35 Engine Air Filter',
    },
  },
  humvee: {
    h1: 'Humvee Air Filter',
  },
  gmc: {
    h1: 'GMC Air Filters',
    intro:
      'Green Filter makes a washable, reusable GMC air filter engineered as a high-flow performance engine air filter for GMC Terrain, Acadia, Sierra, Canyon, and Envoy models. Whether you need a GMC Terrain air filter, a GMC Acadia air filter, a GMC Sierra 1500 air filter, a GMC Canyon air filter, or a GMC Envoy air filter, find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      Acadia: 'GMC Acadia Engine Air Filter',
      Sierra: 'GMC Sierra Engine Air Filter',
      Terrain: 'GMC Terrain Engine Air Filter',
    },
  },
  bmw: {
    h1: 'BMW Air Filters',
    intro:
      'Green Filter makes a washable, reusable BMW air filter engineered as a high-flow performance engine air filter for BMW 330i, 335i, M3, X1, X3, and Z4 models, plus MINI Cooper models. Whether you need a BMW 330i air filter, a BMW 335i air filter, a BMW M3 air filter, a BMW X1 air filter, or a BMW Z4 air filter, find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      X1: 'BMW X1 Engine Air Filter',
      '328i': 'BMW 328i Engine Air Filter',
      '330i': 'BMW 330i Engine Air Filter',
      'Mini Cooper': 'MINI Cooper Engine Air Filter',
      X3: 'BMW X3 Engine Air Filter',
    },
  },
  audi: {
    h1: 'Audi Air Filters',
    intro:
      'Green Filter makes a washable, reusable Audi air filter engineered as a high-flow performance engine air filter for Audi A4, A3, and Q3 models. Whether you need an Audi A4 air filter, an Audi A3 air filter, or an Audi Q3 air filter, find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      Q3: 'Audi Q3 Engine Air Filter',
      A4: 'Audi A4 Engine Air Filter',
    },
  },
  cadillac: {
    h1: 'Cadillac Air Filter',
    intro:
      'Green Filter makes a washable, reusable Cadillac air filter engineered as a high-flow performance engine air filter for Cadillac ATS, CTS, STS, and Escalade models. Whether you need a Cadillac ATS air filter, a Cadillac CTS air filter, a Cadillac STS air filter, or a Cadillac Escalade air filter, find the right Cadillac engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
  },
  chrysler: {
    h1: 'Chrysler Air Filters',
    intro:
      'Green Filter makes a washable, reusable Chrysler air filter engineered as a high-flow performance engine air filter for Chrysler 300, PT Cruiser, and Sebring models. Whether you need a Chrysler 300 air filter, a Chrysler PT Cruiser air filter, or a Chrysler Sebring air filter, find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
  },
  dodge: {
    h1: 'Dodge Air Filters',
    intro:
      'Green Filter makes a washable, reusable Dodge air filter engineered as a high-flow performance engine air filter for Dodge Charger, Challenger, Ram 1500, and Dart models. Whether you need a Dodge Charger air filter, a Dodge Challenger air filter, a Dodge Ram 1500 air filter, or a Dodge Dart air filter, find the right fit by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      Dart: 'Dodge Dart Engine Air Filter',
    },
  },
  ford: {
    h1: 'Ford Air Filters',
    intro:
      'Green Filter makes a washable, reusable Ford air filter engineered as a high-flow performance engine air filter for Ford Escape, Explorer, Focus, Bronco, Expedition, Ranger, Fiesta, Mustang, and F-250 models. Whether you need a Ford Escape air filter, a Ford Explorer air filter, a Ford Focus air filter, a Ford Bronco air filter, a Ford Expedition air filter, a Ford Ranger air filter, a Ford Fiesta air filter, a Ford Mustang air filter, or a Ford F-250 air filter, find the right Ford engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      Edge: 'Ford Edge Engine Air Filter',
      Escape: 'Ford Escape Engine Air Filter',
      Fusion: 'Ford Fusion Engine Air Filter',
      Bronco: 'Ford Bronco Engine Air Filter',
      Explorer: 'Ford Explorer Engine Air Filter',
      Focus: 'Ford Focus Engine Air Filter',
      Expedition: 'Ford Expedition Engine Air Filter',
      Maverick: 'Ford Maverick Engine Air Filter',
      EcoSport: 'Ford EcoSport Engine Air Filter',
    },
  },
  honda: {
    h1: 'Honda Air Filters',
    intro:
      'Green Filter makes a washable, reusable Honda air filter engineered as a high-flow performance engine air filter for Honda Civic, Civic Si, Element, and CR-V models. Whether you need a Honda Civic air filter, a Honda Civic Si air filter, a Honda Element air filter or Honda Element engine air filter, or a Honda CR-V air filter, find the right Honda engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      'Civic Si': 'Honda Civic Si Air Filter',
      Element: 'Honda Element Air Filter',
      'CR-V': 'Honda CR-V Air Filter',
    },
  },
  volkswagen: {
    h1: 'Volkswagen Air Filter',
    intro:
      'Green Filter makes a washable, reusable Volkswagen air filter engineered as a high-flow performance engine air filter for Volkswagen Beetle, Golf, Jetta, and Passat models. Whether you need a VW air filter, a Volkswagen Beetle air filter, a Volkswagen Golf air filter, a Volkswagen Jetta air filter, a Volkswagen Passat air filter, or a VW engine air filter, find the right Volkswagen engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      Jetta: 'Volkswagen Jetta Engine Air Filters',
    },
  },
  toyota: {
    h1: 'Toyota Air Filter',
    intro:
      'Green Filter makes a washable, reusable Toyota air filter engineered as a high-flow performance engine air filter for Toyota Camry, Corolla, Tacoma, Tundra, 4Runner, Highlander, Sienna, Avalon Hybrid, Land Cruiser, and Sequoia models. Whether you need a Toyota Camry air filter, a Toyota Corolla air filter, a Toyota Tacoma air filter, a Toyota Tundra air filter, a Toyota 4Runner air filter, a Toyota Highlander air filter, a Toyota Land Cruiser air filter, or a Toyota Sequoia air filter, find the right Toyota engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      '4Runner': 'Toyota 4Runner Engine Air Filters',
      Camry: 'Toyota Camry Engine Air Filters',
      Corolla: 'Toyota Corolla Engine Air Filters',
      Highlander: 'Toyota Highlander Engine Air Filters',
      Sienna: 'Toyota Sienna Engine Air Filters',
      Tacoma: 'Toyota Tacoma Engine Air Filters',
      Tundra: 'Toyota Tundra Engine Air Filters',
      'Avalon Hybrid': 'Toyota Avalon Hybrid Engine Air Filters',
    },
  },
  subaru: {
    h1: 'Subaru Engine Air Filter',
    intro:
      'Green Filter makes a washable, reusable Subaru air filter engineered as a high-flow performance engine air filter for Subaru Forester, Outback, BRZ, Impreza, WRX, WRX STI, Legacy, and Baja models. Whether you need a Subaru Forester air filter, a Subaru Outback air filter, a Subaru BRZ air filter, a Subaru Impreza air filter, a Subaru WRX air filter, a Subaru WRX STI air filter, a Subaru Legacy air filter, or a Subaru Baja air filter, find the right Subaru engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      BRZ: 'Subaru BRZ Engine Air Filters',
      Forester: 'Subaru Forester Engine Air Filters',
      Outback: 'Subaru Outback Engine Air Filters',
    },
  },
  scion: {
    h1: 'Scion Air Filter',
    intro:
      'Green Filter makes a washable, reusable Scion air filter engineered as a high-flow performance engine air filter for Scion FR-S models. Find the right Scion FR-S air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
  },
  pontiac: {
    h1: 'Pontiac Air Filter',
    intro:
      'Green Filter makes a washable, reusable Pontiac air filter engineered as a high-flow performance engine air filter for Pontiac Grand Am, Grand Prix, and Vibe models. Whether you need a Pontiac Grand Am air filter, a Pontiac Grand Prix air filter, or a Pontiac Vibe air filter, find the right Pontiac engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
  },
  mazda: {
    h1: 'Mazda Engine Air Filter',
    intro:
      'Green Filter makes a washable, reusable Mazda air filter engineered as a high-flow performance engine air filter for Mazda3, CX-5, and CX-7 models. Whether you need a Mazda 3 air filter, a Mazda CX-5 air filter, or a Mazda CX-7 air filter, find the right Mazda engine air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
    modelHeadings: {
      '3': 'Mazda3 Engine Air Filters',
      'CX-7': 'Mazda CX-7 Engine Air Filters',
    },
  },
  lincoln: {
    h1: 'Lincoln Air Filter',
    intro:
      'Green Filter makes a washable, reusable Lincoln air filter engineered as a high-flow performance engine air filter for Lincoln MKC, MKZ, Navigator, and Mark VII models. Find the right Lincoln air filter by year, engine, and part number - clean it, re-oil it, and reuse it instead of buying a new paper filter.',
  },
  lexus: {
    modelHeadings: {
      'ES 250': 'Lexus ES 250 Engine Air Filter',
      'IS 250': 'Lexus is250 Engine Air Filter',
      RX350: 'Lexus rx350 Engine Air Filter',
    },
  },
  jeep: {
    h1: 'Jeep Engine Air Filters',
    modelsHeading: 'Find a Green Filter for Your Jeep',
    modelsParagraph:
      'Select your Jeep model below to view compatible Green Filter engine air filters by year, engine size, and application.',
  },
};
